import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from '@radix-ui/react-slot'
import type { ComponentProps } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Buttons are square-cornered on purpose: the whole identity is built from
 * hard edges, hairlines and condensed capitals. Nothing here is rounded.
 */
const button = cva(
  'relative inline-flex items-center justify-center gap-2 font-condensed uppercase tracking-[0.08em] whitespace-nowrap transition-all duration-200 disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blood-bright',
  {
    variants: {
      variant: {
        primary:
          'bg-blood text-bone hover:bg-blood-bright active:translate-y-px shadow-[0_0_0_0_var(--color-blood)] hover:shadow-[0_6px_24px_-6px_var(--color-blood)]',
        outline:
          'border border-hairline bg-transparent text-bone hover:border-blood hover:text-bone hover:bg-blood/10',
        ghost: 'bg-transparent text-bone-dim hover:text-bone hover:bg-bone/5',
        bone: 'bg-bone text-ink hover:bg-white',
        danger: 'border border-blood/40 bg-blood/10 text-blood-bright hover:bg-blood/20',
      },
      size: {
        sm: 'h-8 px-3 text-[0.6875rem]',
        md: 'h-11 px-5 text-[0.8125rem]',
        lg: 'h-14 px-8 text-[0.9375rem]',
        icon: 'size-9',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  }
)

interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof button> {
  asChild?: boolean
  loading?: boolean
}

export function Button({
  className,
  variant,
  size,
  asChild,
  loading,
  disabled,
  children,
  ...props
}: ButtonProps) {
  // Slot forwards props onto a single child element, so it cannot also receive
  // a sibling spinner or a `disabled` attribute meant for a <button>.
  if (asChild) {
    return (
      <Slot className={cn(button({ variant, size }), className)} {...props}>
        {children}
      </Slot>
    )
  }

  return (
    <button
      className={cn(button({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
      {children}
    </button>
  )
}

export { button as buttonVariants }
